/**
 * Einheitliches "Anzeigen"-Modal fuer PDF-Dokumente (TQ #960).
 *
 * Wird fuer Angebote, Rechnungen, Vertraege (AVV) und Regieberichte genutzt:
 * Blob laden -> Object-URL -> <iframe> als Vorschau -> "PDF speichern" (<a download>).
 * Die Komponente wird nur gemountet, solange das Modal offen ist; der Ladevorgang
 * startet beim Mounten, die Object-URL wird beim Schliessen/Unmount freigegeben.
 *
 * Fehler werden sichtbar im Modal angezeigt (kein Toast) statt still zu verschwinden.
 * HTTP 403 zeigt den sachlichen Text aus forbiddenText (TQ #890) als ruhigen Hinweis.
 * Bei responseType 'blob' kommt auch der Fehler-Body als Blob an: er wird erst als
 * JSON gelesen ({"error": "..."}), bevor forbiddenText ihn prueft.
 * Hinweis CSP: die Vorschau braucht `frame-src blob:` (wird im Helm-Chart gesetzt).
 */
import { useEffect, useRef, useState } from 'react'
import { forbiddenText } from '../lib/forbiddenText'

export interface PdfViewerModalProps {
  /** Titel im Kopf, z. B. "Angebot SAL-QTN-0001". */
  title: string
  /** Dateiname fuer "PDF speichern" (ohne Pfadzeichen; '/' wird ersetzt). */
  filename: string
  /** Liefert das PDF als Blob; wirft bei Fehlern (axios-Fehler mit response.status). */
  load: () => Promise<Blob>
  onClose: () => void
}

/**
 * Liest bei einem axios-Fehler mit Blob-Body (responseType 'blob') den Body als JSON und
 * liefert eine Kopie des Fehlers mit response.data = geparstes Objekt. Sonst unveraendert.
 */
export async function withJsonErrorBody(e: unknown): Promise<unknown> {
  const res = (e as { response?: { data?: unknown } } | null)?.response
  const data = res?.data as { text?: () => Promise<string> } | undefined
  if (!res || typeof Blob === 'undefined' || !(data instanceof Blob)) return e
  try {
    const text = typeof data.text === 'function'
      ? await data.text()
      : await new Promise<string>((resolve, reject) => {
          const r = new FileReader()
          r.onload = () => resolve(String(r.result ?? ''))
          r.onerror = () => reject(r.error)
          r.readAsText(data as Blob)
        })
    return { ...(e as object), response: { ...res, data: JSON.parse(text) } }
  } catch {
    return { ...(e as object), response: { ...res, data: undefined } }
  }
}

/** Fehlertext fuer das Modal; 403 -> forbiddenText (sachlich), sonst Status-Text. */
export function describeLoadError(e: unknown): string {
  const forbidden = forbiddenText(e)
  if (forbidden) return forbidden
  const err = e as { response?: { status?: number } }
  const status = err?.response?.status
  if (status === 404) return 'Zu diesem Dokument wurde kein PDF gefunden.'
  if (status) return 'Das PDF konnte gerade nicht geladen werden. Bitte versuchen Sie es später noch einmal.'
  return 'Keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.'
}

const btn: React.CSSProperties = {
  padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6,
  background: '#fff', cursor: 'pointer', fontSize: '0.8125rem',
  color: '#1f2937', textDecoration: 'none',
}

export default function PdfViewerModal({ title, filename, load, onClose }: PdfViewerModalProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState<{ text: string; forbidden: boolean } | null>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    let cancelled = false
    let url: string | null = null
    loadRef.current().then(
      (data) => {
        if (cancelled) return
        url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        setObjectUrl(url)
      },
      async (e) => {
        const err = await withJsonErrorBody(e)
        if (!cancelled) setError({ text: describeLoadError(err), forbidden: forbiddenText(err) !== null })
      },
    )
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [])

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '2rem',
      }}>
      <div role="dialog" aria-modal="true" aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, width: '90%', maxWidth: 1100,
          height: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb',
        }}>
          <strong>{title}</strong>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {objectUrl && (
              <a href={objectUrl} download={`${filename.replace(/\//g, '-')}.pdf`} style={btn}>
                ⬇ PDF speichern
              </a>
            )}
            <button ref={closeRef} type="button" onClick={onClose} style={btn}>Schließen</button>
          </div>
        </div>
        <div style={{ flex: 1, background: '#f3f4f6' }}>
          {objectUrl ? (
            <iframe src={objectUrl} title={title} style={{ width: '100%', height: '100%', border: 'none' }} />
          ) : error ? (
            <div role={error.forbidden ? 'status' : 'alert'} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: error.forbidden ? '#6b7280' : '#991b1b', padding: '1rem', textAlign: 'center',
            }}>{error.text}</div>
          ) : (
            <div role="status" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: '#6b7280',
            }}>Lade PDF …</div>
          )}
        </div>
      </div>
    </div>
  )
}
